import {
  type EntityRootData,
  type ConfigEntityRootData,
  type GenerateDomainEntityId,
  generateId,
} from '@web-config-app/core';

/**
 * Injects a domain entity ID into the provided entity data.
 *
 * This function either preserves an existing domain entity ID if one is present
 * in the input data, or generates a new ID based on the provided configuration.
 *
 * @param generateDomainEntityId - Configuration object containing settings for domain entity ID generation
 * @param generateDomainEntityId.size - The length of the generated domain entity ID
 * @param generateDomainEntityId.alphabet - The character set to use for generating the domain entity ID
 * @param generateDomainEntityId.enabled - Flag indicating if domain entity ID injection is enabled
 * @param data - The entity data to which the domain entity ID will be added
 *
 * @returns A new object with the original data and injected domain entity ID in the
 * entityMetadata.domainEntityId path, or an empty object if no data was provided
 *
 * @example
 * const config = {
 *   size: 8,
 *   alphabet: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789',
 *   enabled: true
 * };
 * const entityData = {
 *   attributes: {
 *     entityMetadata: {}
 *   }
 * };
 * const result = injectDomainEntityId(config, entityData);
 * // result.attributes.entityMetadata.domainEntityId will contain a generated ID
 */

export const injectDomainEntityId = (
  generateDomainEntityId: GenerateDomainEntityId,
  data?: EntityRootData | ConfigEntityRootData,
): EntityRootData | ConfigEntityRootData => {
  if (!generateDomainEntityId?.enabled) {
    return data || {};
  }
  // Generate a new ID if there's no existing domain entity ID - if the existing domainEntityId was not meant to be preserved in the operation it will have already been removed
  const domainEntityId =
    data?.attributes?.entityMetadata?.domainEntityId ??
    generateId({
      size: generateDomainEntityId.size,
      alphabet: generateDomainEntityId.alphabet,
    });

  return {
    ...data,
    attributes: {
      ...data?.attributes,
      entityMetadata: {
        ...data?.attributes?.entityMetadata,
        domainEntityId,
        status: data?.attributes?.entityMetadata?.status || 'new',
      },
    },
  };
};
